import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Gift, Envelope, Download, Copy, MagnifyingGlass } from '@phosphor-icons/react';

/* ───────────────────────────────────────────────────────────────
 *  Gmail Voucher Extractor (ADMIN-ONLY)
 *  • Reuses Gmail credentials from amazon_balance_gmail
 *  • Date-range search → Kalyan + Tanishq vouchers (extendable)
 *  • Download as CSV (Excel opens it natively)
 * ─────────────────────────────────────────────────────────────── */
export default function VoucherExtractorPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'owner';

  const today = new Date();
  const thirtyAgo = new Date(today.getTime() - 30 * 24 * 3600 * 1000);
  const iso = (d) => d.toISOString().slice(0, 10);

  const [accounts, setAccounts] = useState([]);
  const [gmail, setGmail] = useState('');
  const [fromDate, setFromDate] = useState(iso(thirtyAgo));
  const [toDate, setToDate] = useState(iso(today));
  const [kinds, setKinds] = useState({ kalyan: true, tanishq: true });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState('all');

  const loadAccounts = useCallback(async () => {
    try {
      const { data } = await api.get('/voucher-extractor/gmail-accounts');
      setAccounts(data || []);
      if (data?.length && !gmail) setGmail(data[0].gmail);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Gmail accounts load fail'); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { if (isAdmin) loadAccounts(); }, [isAdmin, loadAccounts]);

  const extract = async () => {
    if (!gmail) return toast.error('Gmail select karein');
    const kk = Object.entries(kinds).filter(([_, v]) => v).map(([k]) => k);
    if (kk.length === 0) return toast.error('Kam se kam ek voucher type select karein');
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post('/voucher-extractor/extract', {
        gmail, from_date: fromDate, to_date: toDate, kinds: kk,
      });
      setResult(data);
      toast.success(`Mil gaye — Kalyan: ${data.kalyan_count} · Tanishq: ${data.tanishq_count}`);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Extract fail'); }
    finally { setBusy(false); }
  };

  const copyCell = (txt) => { navigator.clipboard.writeText(txt || ''); toast.success('Copied'); };

  const exportCSV = () => {
    if (!result) return;
    const rows = [['Type', 'Subject', 'Date', 'Card Number', 'PIN', 'Amount', 'Validity']];
    const all = [...(result.kalyan || []), ...(result.tanishq || [])];
    all.forEach(v => {
      rows.push([
        v.type, v.subject || '', v.date || '',
        v.card_number || '', v.pin || '', v.amount || 0, v.validity || '',
      ]);
    });
    const csv = rows.map(r => r.map(c => {
      const s = String(c ?? '');
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    }).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `vouchers_${gmail.replace('@', '_')}_${fromDate}_to_${toDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!isAdmin) {
    return <div className="p-6 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded">Admin/Owner only — aapka role: <b>{user?.role}</b></div>;
  }

  const list = tab === 'all'
    ? [...(result?.kalyan || []), ...(result?.tanishq || [])]
    : tab === 'kalyan' ? (result?.kalyan || []) : (result?.tanishq || []);

  return (
    <div className="space-y-5" data-testid="voucher-extractor-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Gift size={24} weight="duotone" className="text-violet-700" />
          Kalyan & Tanishq Voucher Extractor
        </h1>
        <p className="text-sm text-gray-500 mt-0.5">Gmail se Kalyan Jewellers aur Tanishq ke gift vouchers directly extract karein. Admin/Owner only.</p>
      </div>

      <Card>
        <CardContent className="p-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-700">Gmail Account</label>
              <select value={gmail} onChange={e => setGmail(e.target.value)} className="w-full h-9 mt-1 px-2 text-sm border border-gray-300 rounded" data-testid="ve-gmail-select">
                {accounts.length === 0 && <option value="">— none configured —</option>}
                {accounts.map(a => (
                  <option key={a.gmail} value={a.gmail}>
                    {a.label ? `${a.label} — ${a.gmail}` : a.gmail}
                  </option>
                ))}
              </select>
              {accounts.length === 0 && (
                <p className="text-[10px] text-amber-700 mt-1">Pehle Amazon Balance → Accounts me Gmail app-password save karo</p>
              )}
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-700">From Date</label>
              <Input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="h-9 mt-1" data-testid="ve-from" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-700">To Date</label>
              <Input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="h-9 mt-1" data-testid="ve-to" />
            </div>
            <div className="flex items-end gap-2">
              <Button onClick={extract} disabled={busy || !gmail} className="flex-1 bg-violet-600 hover:bg-violet-700" data-testid="ve-extract-btn">
                <MagnifyingGlass size={14} className="mr-1" /> {busy ? 'Extracting…' : 'Extract Vouchers'}
              </Button>
            </div>
          </div>
          <div className="flex gap-4 text-xs">
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input type="checkbox" checked={kinds.kalyan} onChange={e => setKinds(p => ({ ...p, kalyan: e.target.checked }))} data-testid="ve-kalyan" />
              <span className="font-semibold text-amber-700">Kalyan Jewellers</span>
            </label>
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input type="checkbox" checked={kinds.tanishq} onChange={e => setKinds(p => ({ ...p, tanishq: e.target.checked }))} data-testid="ve-tanishq" />
              <span className="font-semibold text-rose-700">Tanishq</span>
            </label>
            <div className="ml-auto text-gray-500 flex items-center gap-1">
              <Envelope size={13} /> Uses server-side IMAP (secure — no API key exposed in browser)
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {result && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard label="Kalyan" val={result.kalyan_count} cls="bg-amber-50 text-amber-800 border-amber-200" />
            <StatCard label="Tanishq" val={result.tanishq_count} cls="bg-rose-50 text-rose-800 border-rose-200" />
            <StatCard label="Total Vouchers" val={(result.kalyan_count || 0) + (result.tanishq_count || 0)} cls="bg-violet-50 text-violet-800 border-violet-200" />
            <StatCard label="Total Amount" val={`₹${Math.round(result.total_amount || 0).toLocaleString('en-IN')}`} cls="bg-emerald-50 text-emerald-800 border-emerald-200" />
          </div>

          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex gap-1">
              {[
                { k: 'all', label: `All (${(result.kalyan_count || 0) + (result.tanishq_count || 0)})` },
                { k: 'kalyan', label: `Kalyan (${result.kalyan_count || 0})` },
                { k: 'tanishq', label: `Tanishq (${result.tanishq_count || 0})` },
              ].map(t => (
                <button key={t.k} onClick={() => setTab(t.k)}
                  className={`text-xs px-3 py-1.5 rounded border transition-colors ${tab === t.k ? 'bg-violet-600 text-white border-violet-600' : 'bg-white border-gray-300 hover:bg-gray-50'}`}
                  data-testid={`ve-tab-${t.k}`}>
                  {t.label}
                </button>
              ))}
            </div>
            <Button onClick={exportCSV} variant="outline" size="sm" data-testid="ve-export" className="gap-1">
              <Download size={14} /> Export CSV (Excel)
            </Button>
          </div>

          <Card>
            <CardContent className="p-0 overflow-x-auto">
              <Table className="table-dense">
                <TableHeader>
                  <TableRow>
                    <TableHead>Type</TableHead>
                    <TableHead>Subject</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Card Number</TableHead>
                    <TableHead>PIN</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Validity</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {list.length === 0 ? (
                    <TableRow><TableCell colSpan={7} className="text-center py-8 text-gray-500">Koi voucher match nahi hua is filter me</TableCell></TableRow>
                  ) : list.map((v, i) => (
                    <TableRow key={i} data-testid={`ve-row-${i}`}>
                      <TableCell>
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${v.type === 'kalyan' ? 'bg-amber-100 text-amber-800' : 'bg-rose-100 text-rose-800'}`}>
                          {v.type?.toUpperCase()}
                        </span>
                      </TableCell>
                      <TableCell className="text-xs max-w-[280px] truncate" title={v.subject}>{v.subject}</TableCell>
                      <TableCell className="text-[10px] text-gray-500">{v.date ? new Date(v.date).toLocaleDateString('en-IN') : '-'}</TableCell>
                      <TableCell className="text-xs font-mono">
                        {v.card_number ? (
                          <button onClick={() => copyCell(v.card_number)} className="hover:text-violet-700 flex items-center gap-1" title="Copy">
                            {v.card_number} <Copy size={11} />
                          </button>
                        ) : <span className="text-gray-300">-</span>}
                      </TableCell>
                      <TableCell className="text-xs font-mono">
                        {v.pin ? (
                          <button onClick={() => copyCell(v.pin)} className="hover:text-violet-700 flex items-center gap-1" title="Copy">
                            {v.pin} <Copy size={11} />
                          </button>
                        ) : <span className="text-gray-300">-</span>}
                      </TableCell>
                      <TableCell className="text-right text-xs font-bold text-emerald-700">{v.amount > 0 ? `₹${Math.round(v.amount).toLocaleString('en-IN')}` : '-'}</TableCell>
                      <TableCell className="text-[10px] text-gray-600">{v.validity || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {result.errors?.length > 0 && (
            <div className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
              <b>{result.errors.length} minor errors</b>: {result.errors[0]}…
            </div>
          )}
        </>
      )}
    </div>
  );
}


function StatCard({ label, val, cls }) {
  return (
    <div className={`p-3 rounded-lg border ${cls} text-center`}>
      <div className="text-[10px] font-bold uppercase">{label}</div>
      <div className="text-2xl font-bold mt-0.5">{val}</div>
    </div>
  );
}
